import React, { useState, useRef, useCallback, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';
import type { RepoStackAnalysis } from './types';
import { AppMapGraph, type AppMapHandle } from './app-map/AppMapGraph';
import { ToolPanel } from './shared/ToolPanel';
import { LanguageFilterSidebar } from './shared/LanguageFilterSidebar';
import { MapHeaderStrip } from './shared/MapHeaderStrip';

interface KnowledgeGraphViewProps {
  data: RepoStackAnalysis;
  jobId: string;
}

export const KnowledgeGraphView: React.FC<KnowledgeGraphViewProps> = ({ data, jobId }) => {
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<Set<string>>(new Set());
  const appMapRef = useRef<AppMapHandle>(null);

  const handleToolSelect = useCallback((toolId: string) => {
    setSelectedToolId(toolId || null);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedToolId(null);
  }, []);

  const toggleLanguage = useCallback((id: string) => {
    setSelectedLanguageIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearLanguages = useCallback(() => {
    setSelectedLanguageIds(new Set());
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showInfoModal) setShowInfoModal(false);
        else handleClosePanel();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleClosePanel, showInfoModal]);

  return (
    <div className="relative w-full h-[calc(100vh-96px)] overflow-hidden bg-white dark:bg-gray-900">
      <LanguageFilterSidebar
        data={data}
        selectedLanguageIds={selectedLanguageIds}
        onToggleLanguage={toggleLanguage}
        onClear={clearLanguages}
      />

      <MapHeaderStrip data={data} jobId={jobId} />

      <div className="absolute left-56 right-0 top-14 bottom-0">
        <AppMapGraph
          ref={appMapRef}
          data={data}
          selectedToolId={selectedToolId}
          onToolSelect={handleToolSelect}
          selectedLanguageIds={selectedLanguageIds}
        />
      </div>

      <ToolPanel
        toolId={selectedToolId}
        data={data}
        onClose={handleClosePanel}
        onSelectTool={handleToolSelect}
      />

      <button
        onClick={() => setShowInfoModal(true)}
        className="absolute top-[3.5rem] left-[15rem] mt-2 ml-2 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-slate-800/80 border border-slate-700/50 text-slate-400 hover:text-slate-200 hover:border-slate-600 transition-colors backdrop-blur-sm"
        aria-label="What is App Map?"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {showInfoModal && (
        <div
          className="absolute inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className="relative bg-slate-900 border border-slate-700/50 rounded-xl p-6 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowInfoModal(false)}
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-md border border-slate-700/50 bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-base font-semibold text-white mb-3">About App Map</h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-2">
              Your delivery pipeline as a knowledge graph. Each column is an SDLC stage; each card
              is a tool. The row of circles up top is a stage-level health summary.
            </p>
            <p className="text-sm text-slate-300 leading-relaxed mb-2">
              Dashed teal cards are <em>gaps</em> — tools we suggest you add. Curves between cards
              are relationships: red dashes mean <em>conflicts</em>, solid gray means{' '}
              <em>depends on</em>. Other relationship types appear only when you focus a card or
              stage.
            </p>
            <p className="text-sm text-slate-300 leading-relaxed">
              Hover a card to preview its relationships. Click a card to open details.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
